import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type StatusValue =
  | 'active' | 'approved' | 'published'
  | 'pending' | 'in_review' | 'in_progress' | 'suspended' | 'sent_back' | 'high_risk'
  | 'draft' | 'not_started'
  | 'cancelled' | 'critical' | 'rejected'
  | 'trial' | 'new' | 'informational'
  | 'offboarding' | 'offboarded'
  | 'excellent' | 'good'
  | 'fair'
  | 'poor'
  | 'issued' | 'submitted' | 'under_review' | 'expired'
  | 'high' | 'medium' | 'low'
  | 'needs_follow_up' | 'not_reviewed'
  | string;

export function getStatusVariant(status: StatusValue): BadgeProps['variant'] {
  if (!status) return 'muted';
  switch (status.toLowerCase()) {
    case 'active':
    case 'approved':
    case 'published':
    case 'excellent':
    case 'good':
    case 'closed_mitigated':   // RA.4: terminal closed state — positive closure
      return 'emerald';

    case 'pending':
    case 'pending_approval':    // RA/ORA: awaiting approver action (A5 ORA.9)
    case 'in_review':
    case 'in_progress':
    case 'suspended':
    case 'sent_back':
    case 'returned':
    case 'high_risk':
    case 'poor':
    case 'high':
    case 'expiring_soon':
      return 'amber';

    case 'draft':
    case 'not_started':
    case 'medium':
    case 'low':
      return 'muted';

    case 'cancelled':
    case 'critical':
    case 'rejected':
    case 'denied':             // RA/ORA: approver-rejected — negative outcome (A5 ORA.9)
    case 'expired':
    case 'revoked':            // RA.4: terminal revoked state — negative closure
    case 'withdrawn':          // ⚠ SS2B: risk-response submitter-cancelled (additive; ORA-safe)
      return 'rose';

    // vuln statuses — outlined to distinguish from solid severity badges
    case 'open':
      return 'outline-amber';

    case 'in_remediation':
      return 'outline-muted';

    case 'patched':
      return 'outline-emerald';

    case 'accepted':
      return 'outline-sky';

    case 'trial':
    case 'new':
    case 'informational':
    case 'issued':
      return 'default';

    case 'offboarding':
    case 'submitted':
    case 'resubmitted':
    case 'reopened':
    case 'under_review':
      return 'violet';

    // D-VENDOR-LIFECYCLE.2-PE-5: offboarded → muted (neutral/archived; resolves violet collision with under_review)
    case 'offboarded':
      return 'muted';

    case 'fair':
    case 'bypassed':
    case 'replaced_by_renewal': // ⚠ SS2B: risk-response superseded by a renewal — neutral, NOT a failure (additive)
      return 'sky';

    case 'needs_follow_up':
      return 'amber';

    case 'not_reviewed':
      return 'muted';

    default:
      return 'muted';
  }
}

function formatStatusLabel(status: string): string {
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface StatusBadgeProps {
  status: StatusValue;
  label?: string;
  className?: string;
}

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  return (
    <Badge variant={getStatusVariant(status)} className={cn(className)}>
      {label ?? formatStatusLabel(status)}
    </Badge>
  );
}
